import { useCallback, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useOutletContext, useParams } from 'react-router-dom';
import { confirmTerms, getTimeline, getTransaction, listEvidence, type PortalTransaction } from '../api';
import { useAuth } from '../auth';
import { revisionsOf, workspaceOf } from '../workspace';
import {
  clickPortalConfirmTerms,
  createPortalCommandLock,
  portalWorkspacePrimaryActions,
} from '../workspace-primary-action';
import { usePortalWorkspaceRefresh } from '../workspace-refresh';

export function WorkspacePage() {
  const { id = '' } = useParams();
  const { user } = useAuth();
  const [item, setItem] = useState<PortalTransaction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const commandLock = useRef(createPortalCommandLock());

  const load = useCallback(() => {
    if (!id) return Promise.resolve();
    return getTransaction(id)
      .then((result) => {
        setItem(result.data);
        setError(null);
      })
      .catch((caught: unknown) => {
        setError(caught instanceof Error ? caught.message : 'Could not load this PackProof.');
      });
  }, [id]);

  usePortalWorkspaceRefresh(load, {
    processingState: item ? workspaceOf(item).evidenceProcessing.state : 'IDLE',
    revisions: item ? revisionsOf([item]) : [],
    transactionIds: id ? [id] : [],
  });

  if (error) return <p className="error">{error}</p>;
  if (!item || !user) return <p className="meta">Loading…</p>;

  const workspace = workspaceOf(item);
  const next = workspace.nextAction;
  const actions = portalWorkspacePrimaryActions({
    transactionId: item.id,
    primaryAction: next.primaryAction,
    proofAvailable: workspace.proof.availability === 'AVAILABLE',
  });

  const onConfirmTerms = () => {
    void clickPortalConfirmTerms({
      lock: commandLock.current,
      transactionId: item.id,
      confirmTerms,
      reloadAuthoritativeWorkspace: load,
      setPending: setConfirming,
      setError: setActionError,
    });
  };

  return (
    <>
      <p className="eyebrow">{next.humanStateLabel}</p>
      <h1>{item.title}</h1>
      <p className="lede">{next.headline} {next.instruction}</p>
      <div className="row">
        {actions.capture ? <Link className="btn" to={actions.capture.href}>{actions.capture.label}</Link> : null}
        {actions.proof ? (
          <Link
            className={actions.proof.prominence === 'secondary' ? 'btn secondary' : 'btn'}
            to={actions.proof.href}
          >
            {actions.proof.label}
          </Link>
        ) : null}
        {actions.command?.action === 'CONFIRM_TERMS' ? (
          <button type="button" className="btn" disabled={confirming} onClick={onConfirmTerms}>
            {confirming ? 'Confirming…' : actions.command.label}
          </button>
        ) : null}
      </div>
      {actions.mobileOnly ? <p className="meta">{actions.mobileOnly.label}</p> : null}
      {actionError ? <p className="error">{actionError}</p> : null}
      <nav className="tabs" aria-label="Workspace">
        <NavLink to={`/packproofs/${item.id}`} end>Overview</NavLink>
        <NavLink to={`/packproofs/${item.id}/activity`}>Activity</NavLink>
        <NavLink to={`/packproofs/${item.id}/evidence`}>Evidence</NavLink>
        <NavLink to={`/packproofs/${item.id}/proof`}>Proof</NavLink>
      </nav>
      <Outlet context={{ item, viewerId: user.uid }} />
    </>
  );
}

export function WorkspaceOverview() {
  const { item } = useOutletContext<{ item: PortalTransaction; viewerId: string }>();
  const next = workspaceOf(item).nextAction;
  return (
    <div className="stack">
      <article className="card">
        <h2>Status</h2>
        <p>{next.description}</p>
        <p className="meta">{item.source?.platform} {item.source?.externalOrderId ? `• Order ${item.source.externalOrderId}` : ''}</p>
      </article>
    </div>
  );
}

export function WorkspaceActivity() {
  const { id = '' } = useParams();
  const [events, setEvents] = useState<Array<{ id: string; type: string; summary: string; occurredAt: string }>>([]);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => {
    if (!id) return Promise.resolve();
    return getTimeline(id)
      .then((result) => {
        setEvents(result.data);
        setError(null);
      })
      .catch((caught: unknown) => setError(caught instanceof Error ? caught.message : 'Could not load activity.'));
  }, [id]);
  usePortalWorkspaceRefresh(load, { transactionIds: id ? [id] : [] });
  return (
    <div className="stack">
      {error ? <p className="error">{error}</p> : null}
      {events.map((event) => (
        <article className="card" key={event.id}>
          <h2>{event.summary || event.type}</h2>
          <p className="meta">{event.occurredAt}</p>
        </article>
      ))}
      {!events.length && !error ? <p className="meta">No activity yet.</p> : null}
    </div>
  );
}

export function WorkspaceEvidence() {
  const { id = '' } = useParams();
  const [artifacts, setArtifacts] = useState<Array<{
    id: string;
    type: string;
    status: string;
    sha256: string | null;
    workflowReady: boolean;
    finalizedAt: string | null;
  }>>([]);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => {
    if (!id) return Promise.resolve();
    return listEvidence(id)
      .then((result) => {
        setArtifacts(result.data);
        setError(null);
      })
      .catch((caught: unknown) => setError(caught instanceof Error ? caught.message : 'Could not load evidence metadata.'));
  }, [id]);
  usePortalWorkspaceRefresh(load, { transactionIds: id ? [id] : [] });
  return (
    <div className="stack">
      {error ? <p className="error">{error}</p> : null}
      <article className="card">
        <h2>Evidence inventory</h2>
        <p>This list is server metadata. Native Storage locations are never shown. Authorized media streaming is a later slice.</p>
      </article>
      {artifacts.map((item) => (
        <article className="card" key={item.id}>
          <h2>{item.type.replaceAll('_', ' ')}</h2>
          <p className="meta">{item.status.replaceAll('_', ' ')} · {item.workflowReady ? 'workflow ready' : 'not workflow ready'}</p>
          {item.finalizedAt ? <p className="meta">Finalized {item.finalizedAt}</p> : null}
          <p className="hash">{item.sha256 ?? 'Hash not available'}</p>
        </article>
      ))}
      {!artifacts.length && !error ? <p className="meta">No evidence artifacts yet.</p> : null}
    </div>
  );
}
